import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { fetchLoggedUserInfo } from "../mmtUserList/mmtApi";
import { clearStoredSession, getStoredSession } from "../mmtUserList/mmtFlow";
import { fetchPopulationTrends } from "../../services/populationApi";
import { ENV } from "../../configs/environment";
import { validatePopulationAggregate } from "./populationAggregate";
import { resolveCustomerType } from "../../utlis/personTerminology";
import { getCurrentLocale } from "../../utlis/locale";
import { useT } from "../../i18n/useT";
import { useSection } from "../../i18n/useSection";
import { isStatsEligible } from "./peopleStatsData";
import { buildStatsSrcDoc } from "./statsIframeTemplate";
import "./peopleStats.scss";

export default function PeopleStats() {
  const [session] = useState(getStoredSession);
  const [timezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const [info, setInfo] = useState(null);
  const [population, setPopulation] = useState(null);
  const [filters, setFilters] = useState({ age: "all", sex: "all" });
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [frameFailed, setFrameFailed] = useState(false);
  // Keep the aggregate iframe mounted while a filtered cohort is loading.
  const frame = useRef(null);
  const navigate = useNavigate();
  const t = useT("population");
  const strings = useSection("population");
  const locale = getCurrentLocale();
  const mode = resolveCustomerType(info);
  const pageTitle = t(
    mode === "senior_living" ? "Resident Stats" : "Client Stats",
  );
  const currentContext = useRef(null);
  currentContext.current = {
    locale,
    strings,
    aggregate: population,
    pageTitle,
    hostOrigin: window.location.origin,
  };

  useEffect(() => {
    if (!session || info) return;
    let cancelled = false;
    fetchLoggedUserInfo(session)
      .then((account) => {
        if (!cancelled) setInfo(account);
      })
      .catch(() => {
        if (!cancelled) setFailed("UPSTREAM_UNAVAILABLE");
      });
    return () => {
      cancelled = true;
    };
  }, [session, attempt, info]);

  useEffect(() => {
    if (!session || !info || !isStatsEligible(mode)) return;
    const controller = new AbortController();
    setBusy(true);
    setFailed(false);
    setFrameFailed(false);
    fetchPopulationTrends({
      session,
      filters,
      timezone,
      signal: controller.signal,
    })
      .then((data) =>
        validatePopulationAggregate(data, {
          mode,
          filters,
          scoringVersion: ENV.POPULATION_SCORING_VERSION,
          timezone,
        }),
      )
      .then((data) => {
        if (!controller.signal.aborted) setPopulation(data);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error.status === 401 || error.code === "UNAUTHENTICATED") {
          clearStoredSession();
          navigate("/login", { replace: true });
          return;
        }
        if (error.code === "POPULATION_NOT_ELIGIBLE") {
          navigate("/user-list", { replace: true });
          return;
        }
        setFailed(error.code || "UPSTREAM_UNAVAILABLE");
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [session, info, mode, filters, timezone, attempt, navigate]);

  // Locale messages update the existing iframe and preserve filters/accordions.
  const sendLocale = () =>
    frame.current?.contentWindow?.postMessage(
      { type: "seniorlife:stats-locale", ...currentContext.current },
      window.location.origin,
    );
  useEffect(() => {
    sendLocale();
  }, [locale, strings, pageTitle]);
  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.source !== frame.current?.contentWindow ||
        event.origin !== window.location.origin
      )
        return;
      if (event.data?.type === "seniorlife:stats-back") navigate("/user-list");
      if (event.data?.type === "seniorlife:stats-ready") {
        sendLocale();
        frame.current?.contentWindow?.postMessage(
          {
            type: "seniorlife:stats-data",
            aggregate: currentContext.current.aggregate,
          },
          window.location.origin,
        );
      }
      if (event.data?.type === "seniorlife:stats-filter") {
        const next = event.data.filters;
        if (
          ["all", "lt75", "75to84", "85plus"].includes(next?.age) &&
          ["all", "Female", "Male"].includes(next?.sex)
        )
          setFilters((current) =>
            current.age === next.age && current.sex === next.sex
              ? current
              : { age: next.age, sex: next.sex },
          );
      }
      if (event.data?.type === "seniorlife:stats-error") setFrameFailed(true);
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [navigate]);

  const srcDoc = useMemo(
    () =>
      population
        ? buildStatsSrcDoc(null, {
            ...currentContext.current,
            mode,
            userName: info?.name || "",
            aggregate: population,
            asOf: population.as_of.date,
          })
        : "",
    [population, mode, info],
  );
  useEffect(() => {
    if (population)
      frame.current?.contentWindow?.postMessage(
        { type: "seniorlife:stats-data", aggregate: population },
        window.location.origin,
      );
  }, [population]);
  const frameDocument = useRef(null);
  if (srcDoc && !frameDocument.current) frameDocument.current = srcDoc;
  if (!session) return <Navigate to="/login" replace />;
  if (info && !isStatsEligible(mode))
    return <Navigate to="/user-list" replace />;
  return (
    <main className="people-stats-route">
      {failed || frameFailed ? (
        <div className="people-stats-state" role="alert">
          <button onClick={() => navigate("/user-list")}>{t("Back")}</button>
          <p>
            {t(
              ["SCORING_VERSION_UNSUPPORTED", "INVALID_QUERY"].includes(failed)
                ? "Stats configuration required"
                : "Unable to load stats",
            )}
          </p>
          <button onClick={() => setAttempt((value) => value + 1)}>
            {t("Retry")}
          </button>
        </div>
      ) : !population ? (
        <div className="people-stats-state" role="status">
          <button onClick={() => navigate("/user-list")}>{t("Back")}</button>
          <p>{t("Loading")}</p>
        </div>
      ) : (
        <>
          {busy && (
            <p role="status" className="people-stats-updating">
              {t("Loading")}
            </p>
          )}
          <iframe
            ref={frame}
            className="people-stats-frame"
            title={pageTitle}
            srcDoc={frameDocument.current}
            onLoad={sendLocale}
          />
        </>
      )}
    </main>
  );
}
